import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";

import { UsersPageFilters, UsersColumn } from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import { Codecs, PageFilterAndColumns } from "org/pageState/types";
import { unwrapPageStateAs } from "org/pageState/utils";
import { USER_EXTRACTOR_KEYS } from "org/user/list/extractorKeys";

import { decodeOrFilter, encodeOrFilter } from "./common";
import { createEncodeColumnFn } from "./controls/common";

// Keep it in sync with the definitionin impero_shareable_links/src/api/page_state/users.rs
const ALL_COLUMNS: UsersColumn[] = [
    USER_EXTRACTOR_KEYS.NAME,
    USER_EXTRACTOR_KEYS.EMAIL,
    USER_EXTRACTOR_KEYS.LOGIN_WITH_PASSWORD,
    USER_EXTRACTOR_KEYS.ROLES,
    USER_EXTRACTOR_KEYS.GROUPS,
    USER_EXTRACTOR_KEYS.DIRECT_CONTROL_ASSIGNMENTS,
    USER_EXTRACTOR_KEYS.PROGRAMS,
    USER_EXTRACTOR_KEYS.PENDING_ACTIVITIES,
    USER_EXTRACTOR_KEYS.TESTING_CONTROL_ASSIGNMENTS,
    USER_EXTRACTOR_KEYS.TESTING_PROGRAMS,
    USER_EXTRACTOR_KEYS.PENDING_TESTING_ACTIVITIES,
    USER_EXTRACTOR_KEYS.REPORTS,
    USER_EXTRACTOR_KEYS.RISK_MANAGEMENT,
    USER_EXTRACTOR_KEYS.PROVISION_STATUS,
];
const encodeUserManagementColumn: (col: string) => UsersColumn =
    createEncodeColumnFn(ALL_COLUMNS);

// Filters
//
function encodeUsersPageFilters(
    filters: Array<SimpleOrFilter>
): UsersPageFilters {
    return {
        name: encodeOrFilter(USER_EXTRACTOR_KEYS.NAME, filters),
        email: encodeOrFilter(USER_EXTRACTOR_KEYS.EMAIL, filters),
        loginWithPassword: encodeOrFilter(
            USER_EXTRACTOR_KEYS.LOGIN_WITH_PASSWORD,
            filters
        ),
        roles: encodeOrFilter(USER_EXTRACTOR_KEYS.ROLES, filters),
        groups: encodeOrFilter(USER_EXTRACTOR_KEYS.GROUPS, filters),
        directControlAssignments: encodeOrFilter(
            USER_EXTRACTOR_KEYS.DIRECT_CONTROL_ASSIGNMENTS,
            filters
        ),
        programs: encodeOrFilter(USER_EXTRACTOR_KEYS.PROGRAMS, filters),
        pendingActivities: encodeOrFilter(
            USER_EXTRACTOR_KEYS.PENDING_ACTIVITIES,
            filters
        ),
        testingControls: encodeOrFilter(
            USER_EXTRACTOR_KEYS.TESTING_CONTROL_ASSIGNMENTS,
            filters
        ),
        testingPrograms: encodeOrFilter(
            USER_EXTRACTOR_KEYS.TESTING_PROGRAMS,
            filters
        ),
        testingActivities: encodeOrFilter(
            USER_EXTRACTOR_KEYS.PENDING_TESTING_ACTIVITIES,
            filters
        ),
        reports: encodeOrFilter(USER_EXTRACTOR_KEYS.REPORTS, filters),
        riskManagement: encodeOrFilter(
            USER_EXTRACTOR_KEYS.RISK_MANAGEMENT,
            filters
        ),
        provisionStatus: encodeOrFilter(
            USER_EXTRACTOR_KEYS.PROVISION_STATUS,
            filters
        ),
    };
}

function decodeUsersPageFilters(
    filters: UsersPageFilters
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];
    if (filters.name) {
        output.push(decodeOrFilter(USER_EXTRACTOR_KEYS.NAME, filters.name));
    }
    if (filters.email) {
        output.push(decodeOrFilter(USER_EXTRACTOR_KEYS.EMAIL, filters.email));
    }
    if (filters.loginWithPassword) {
        output.push(
            decodeOrFilter(
                USER_EXTRACTOR_KEYS.LOGIN_WITH_PASSWORD,
                filters.loginWithPassword
            )
        );
    }
    if (filters.roles) {
        output.push(decodeOrFilter(USER_EXTRACTOR_KEYS.ROLES, filters.roles));
    }
    if (filters.groups) {
        output.push(decodeOrFilter(USER_EXTRACTOR_KEYS.GROUPS, filters.groups));
    }
    if (filters.directControlAssignments) {
        output.push(
            decodeOrFilter(
                USER_EXTRACTOR_KEYS.DIRECT_CONTROL_ASSIGNMENTS,
                filters.directControlAssignments
            )
        );
    }
    if (filters.programs) {
        output.push(
            decodeOrFilter(USER_EXTRACTOR_KEYS.PROGRAMS, filters.programs)
        );
    }
    if (filters.pendingActivities) {
        output.push(
            decodeOrFilter(
                USER_EXTRACTOR_KEYS.PENDING_ACTIVITIES,
                filters.pendingActivities
            )
        );
    }
    if (filters.testingControls) {
        output.push(
            decodeOrFilter(
                USER_EXTRACTOR_KEYS.TESTING_CONTROL_ASSIGNMENTS,
                filters.testingControls
            )
        );
    }
    if (filters.testingPrograms) {
        output.push(
            decodeOrFilter(
                USER_EXTRACTOR_KEYS.TESTING_PROGRAMS,
                filters.testingPrograms
            )
        );
    }
    if (filters.testingActivities) {
        output.push(
            decodeOrFilter(
                USER_EXTRACTOR_KEYS.PENDING_TESTING_ACTIVITIES,
                filters.testingActivities
            )
        );
    }
    if (filters.reports) {
        output.push(
            decodeOrFilter(USER_EXTRACTOR_KEYS.REPORTS, filters.reports)
        );
    }
    if (filters.riskManagement) {
        output.push(
            decodeOrFilter(
                USER_EXTRACTOR_KEYS.RISK_MANAGEMENT,
                filters.riskManagement
            )
        );
    }
    if (filters.provisionStatus) {
        output.push(
            decodeOrFilter(
                USER_EXTRACTOR_KEYS.PROVISION_STATUS,
                filters.provisionStatus
            )
        );
    }
    return output;
}

// Page state
//
type UsersPageStateWithType = PageStateData<"Users"> & { type: "Users" };

function decodeUsersPageState(
    pageState: UsersPageStateWithType
): PageFilterAndColumns {
    const manualPageState = unwrapPageStateAs(pageState, "Users");
    const filter: SimpleAndFilter = {
        filters: decodeUsersPageFilters(manualPageState.filters),
    };
    const visibleColumnIds = new Set(manualPageState.columns);
    return {
        pageType: pageState.type,
        filter,
        visibleColumnIds,
        version: pageState.version ?? 0,
    };
}

function encodeUsersPageState(
    filterAndColumns: PageFilterAndColumns
): UsersPageStateWithType {
    const { filter, visibleColumnIds } = filterAndColumns;

    let filters: Array<SimpleOrFilter> = [];
    if (filter) {
        filters = filter.filters;
    }

    return {
        type: "Users",
        filters: encodeUsersPageFilters(filters),
        columns: Array.from(visibleColumnIds).map(encodeUserManagementColumn),
        version: filterAndColumns.version,
    };
}

const pageStateUsersCodecs: Codecs<
    PageFilterAndColumns,
    UsersPageStateWithType
> = {
    encode: encodeUsersPageState,
    decode: decodeUsersPageState,
};

export {
    encodeUsersPageState,
    decodeUsersPageState,
    pageStateUsersCodecs,
    ALL_COLUMNS,
};
